import Style from './useRemoveConditional.module.scss';
import ExpressionBuilder from '../panels/helpers/expression/expressionBuilder';
import { useCallback, useState } from 'react';
import { Node, useEditor, useNode } from '@craftjs/core';
import { useModal } from 'src/application/hooks/useModal';
import { useSelectNode } from '../panels/hooks/useSelectNode';
import { ConditionalProps } from '../../types/BaseControlTypes';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';

export const useRemoveConditional = () => {
  const { t } = useScopedTranslation('GeneralFormBuilder.RemoveConditionalNodeModal');
  const selectNode = useSelectNode();
  const modal = useModal();

  const [nodes, setNodes] = useState<Node[]>([]);

  const { actions, getNodeDataKey } = useEditor((state) => {
    return {
      getNodeDataKey: (nodeId: string) => state.nodes[nodeId].data.props.dataKey,
    };
  });

  const { id: nodeId } = useNode();

  const removeFromExpression = useCallback(
    (expressionStr: string) => {
      if (!nodeId) return;

      const expression = ExpressionBuilder.fromString(expressionStr);
      const expressionArr = expression.toArray();
      const index = expressionArr.nodes.findIndex((x) => x.dataKey === getNodeDataKey(nodeId));
      if (index === -1) return;

      expressionArr.nodes.splice(index, 1);
      expressionArr.operators.splice(index - 1, 1);
      if (!expressionArr.nodes.length) return undefined;

      const result = ExpressionBuilder.fromArray(expressionArr).toString();
      return result;
    },
    [getNodeDataKey, nodeId],
  );

  const removeNodeHandler = useCallback(() => {
    if (!nodeId) return;

    for (const node of nodes) {
      const props = node.data.props as ConditionalProps;
      const visibleIf = props.visibleIf;
      const enableIf = props.enableIf;

      if (visibleIf?.includes(getNodeDataKey(nodeId))) {
        const result = removeFromExpression(visibleIf);
        actions.setProp(node.id, (p: ConditionalProps) => {
          p.visibleIf = result;
        });
      }

      if (enableIf?.includes(getNodeDataKey(nodeId))) {
        const result = removeFromExpression(enableIf);
        actions.setProp(node.id, (p: ConditionalProps) => {
          p.enableIf = result;
        });
      }
    }

    actions.delete(nodeId);
  }, [actions, getNodeDataKey, nodeId, nodes, removeFromExpression]);

  const getRelatedNodesInfo = useCallback(
    (node: Node) => {
      const { title, dataKey } = node.data.props;
      const { name } = node.data;

      if (title) return t('NodeTitle', { title });
      if (dataKey) return t('NodeDataKey', { dataKey });
      if (name) return t('NodeName', { name });

      return undefined;
    },
    [t],
  );

  const openModalHandler = useCallback(
    async (dependentNodes: Node[]) => {
      setNodes(dependentNodes);
      modal.openModal();
    },
    [modal],
  );

  return {
    start: openModalHandler,
    modal: (
      <DialogModal
        visible={modal.visible}
        onHide={modal.closeModal}
        title={t('Title')}
        containerClassName={Style.confirmationModalContainer}
        footer={{
          leftButton: {
            text: t('Cancel'),
            onClick: modal.closeModal,
          },
          rightButton: {
            text: t('Delete'),
            onClick: removeNodeHandler,
          },
        }}
      >
        <div className={Style.container}>
          <div className={Style.content}>{t('Content')}</div>
          {nodes.map((node) => {
            return (
              <div
                key={node.id}
                onClick={() => {
                  selectNode(node.id);
                  modal.closeModal();
                }}
                className={Style.conditionItem}
              >
                <div className={Style.text}>{getRelatedNodesInfo(node)}</div>
              </div>
            );
          })}
        </div>
      </DialogModal>
    ),
  };
};
